import React, { useState, useCallback, useEffect } from 'react';
import { useMap } from '../map/MapContext';

interface MeasurePoint { lat: number; lng: number; }

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const NM_PER_RADIAN = 3440.065;

/** Haversine distance in nautical miles */
function haversineNm(a: MeasurePoint, b: MeasurePoint): number {
  const dLat = (b.lat - a.lat) * DEG_TO_RAD;
  const dLng = (b.lng - a.lng) * DEG_TO_RAD;
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(a.lat * DEG_TO_RAD) * Math.cos(b.lat * DEG_TO_RAD) * sinLng * sinLng;
  return 2 * Math.asin(Math.sqrt(h)) * NM_PER_RADIAN;
}

/** Initial bearing from a → b (degrees true) */
function bearing(a: MeasurePoint, b: MeasurePoint): number {
  const dLng = (b.lng - a.lng) * DEG_TO_RAD;
  const lat1 = a.lat * DEG_TO_RAD;
  const lat2 = b.lat * DEG_TO_RAD;
  const x = Math.sin(dLng) * Math.cos(lat2);
  const y = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(x, y) * RAD_TO_DEG) + 360) % 360;
}

export const DistanceBearingPanel: React.FC = () => {
  const { map } = useMap();
  const [points, setPoints] = useState<MeasurePoint[]>([]);
  const [measuring, setMeasuring] = useState(false);

  // Capture map clicks when measuring
  useEffect(() => {
    if (!map || !measuring) return;
    const onClick = (e: any) => {
      setPoints((prev) => [...prev, { lat: e.lngLat.lat, lng: e.lngLat.lng }]);
    };
    map.on('click', onClick);
    map.getCanvas().style.cursor = 'crosshair';
    return () => {
      map.off('click', onClick);
      map.getCanvas().style.cursor = '';
    };
  }, [map, measuring]);

  const toggleMeasure = useCallback(() => setMeasuring((prev) => !prev), []);
  const clearAll = useCallback(() => { setPoints([]); setMeasuring(false); }, []);

  // Compute legs
  const legs = points.slice(1).map((p, i) => ({
    from: points[i],
    to: p,
    distNm: haversineNm(points[i], p),
    bearingDeg: bearing(points[i], p),
  }));

  const totalDistNm = legs.reduce((sum, l) => sum + l.distNm, 0);

  return (
    <div>
      <div className="panel-section">
        <div className="panel-section-title">Distance & Bearing Tool</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
          Click points on the map to measure distance and bearing between them.
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            className={`btn ${measuring ? 'btn-primary' : 'btn-secondary'}`}
            onClick={toggleMeasure}
          >
            {measuring ? '⏹ Stop Measuring' : '📏 Start Measuring'}
          </button>
          <button className="btn btn-secondary" onClick={clearAll} disabled={points.length === 0}>Clear</button>
        </div>
      </div>

      {/* Legs table */}
      {legs.length > 0 && (
        <div className="panel-section">
          <div className="panel-section-title">Legs</div>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '4px 6px' }}>Leg</th>
                <th style={{ textAlign: 'right', padding: '4px 6px' }}>Dist (NM)</th>
                <th style={{ textAlign: 'right', padding: '4px 6px' }}>Bearing (°T)</th>
              </tr>
            </thead>
            <tbody>
              {legs.map((l, i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(51,65,85,0.3)' }}>
                  <td style={{ padding: '4px 6px' }}>{i + 1}</td>
                  <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                    {l.distNm.toFixed(1)}
                  </td>
                  <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                    {l.bearingDeg.toFixed(0).padStart(3, '0')}°
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Total */}
      {legs.length > 1 && (
        <div className="panel-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 600 }}>
            <span>Total Distance</span>
            <span style={{ fontFamily: 'var(--font-mono)' }}>{totalDistNm.toFixed(1)} NM</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)' }}>
            <span>≈ {(totalDistNm * 1.15078).toFixed(1)} SM</span>
            <span>≈ {(totalDistNm * 1.852).toFixed(1)} km</span>
          </div>
        </div>
      )}

      {/* Points list */}
      {points.length > 0 && (
        <div className="panel-section">
          <div className="panel-section-title">Waypoints ({points.length})</div>
          {points.map((p, i) => (
            <div key={i} className="list-item" style={{ padding: '4px 6px' }}>
              <span style={{ fontWeight: 600, fontSize: 12, color: 'var(--accent-light)', minWidth: 22 }}>{i + 1}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                {p.lat.toFixed(5)}°, {p.lng.toFixed(5)}°
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
